/**
 * SearchableCombobox — schlanker Autocomplete-Select mit Volltextsuche.
 *
 * Props:
 *   value:        ausgewählte ID (string | "")
 *   onChange:     (id: string) => void
 *   options:      Array<{ id, label, sublabel?, keywords? (string) }>
 *   placeholder:  Text, wenn nichts gewählt ist
 *   disabled:     boolean
 *   emptyText:    Text bei leerer Trefferliste (Standard: „Keine Treffer")
 *   allowClear:   Wenn true, erscheint ein „—" Eintrag (setzt "" zurück)
 *   testId:       data-testid für das Trigger-Button
 */
import { useMemo, useState, useRef, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CaretDown, X } from "@phosphor-icons/react";

export default function SearchableCombobox({
  value = "",
  onChange,
  options = [],
  placeholder = "Wählen…",
  disabled = false,
  emptyText = "Keine Treffer",
  allowClear = false,
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => o.id === value) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      const hay = [
        o.label || "",
        o.sublabel || "",
        o.keywords || "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between font-normal h-9 ${!selected ? "text-slate-500" : ""} ${className}`}
        >
          <span className="truncate">
            {selected
              ? (selected.sublabel
                  ? <span>{selected.label} <span className="text-slate-500 text-xs">· {selected.sublabel}</span></span>
                  : selected.label)
              : placeholder}
          </span>
          <CaretDown size={14} className="opacity-60 ml-2 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        sideOffset={4}
      >
        <div className="p-2 border-b border-slate-200">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suchen…"
            className="h-8 text-sm"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {allowClear && (
            <button
              type="button"
              onClick={() => { onChange?.(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 flex items-center gap-1"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X size={12} /> zurücksetzen
            </button>
          )}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-slate-500">{emptyText}</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onChange?.(o.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                o.id === value ? "bg-teal-50 text-teal-900" : "text-slate-800"
              }`}
              data-testid={testId ? `${testId}-item-${o.id}` : undefined}
            >
              <div className="truncate">{o.label}</div>
              {o.sublabel && (
                <div className="truncate text-xs text-slate-500">{o.sublabel}</div>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
